import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import React from 'react'

import { type SchemaContact, type SchemaGroup } from '@/__generated/es-api'
import CountryFlag from '@/app/component/atom/flag'
import Tooltip from '@/app/component/atom/tooltip'
import IconText from '@/app/component/layout/icon-text'
import { formatDateTime, formatName, formatPhoneNumber, parsePhoneNumber } from '@/app/service/util/format'

type MobileContactSubCardProps = {
  contact: SchemaContact
  isSelected: boolean
  selectionDisabledReason: string

  onSelect: (contactId: number, checked: boolean) => void
}

type MobileGroupSegmentSubCardProps = {
  group: SchemaGroup
  isSelected: boolean
  root: 'groups' | 'segments'
  selectionDisabledReason: string
  timezone: string

  onSelect: (groupId: number, checked: boolean) => void
}

type MobileGroupSubCardProps = Omit<MobileGroupSegmentSubCardProps, 'root'>

const MobileContactSubCard: React.FC<MobileContactSubCardProps> = (props) => {
  const { contact, isSelected, selectionDisabledReason, onSelect } = props
  const { activeId = 0, doneCount = 0, errorCount = 0, totalCount = 0, unsubbedCount = 0 } = contact.subscription || {}
  const contactId = contact.id!
  const contactName = formatName(contact.firstName, contact.lastName)
  const isSelectionDisabled = !!selectionDisabledReason

  const handleSelect = () => {
    if (!isSelectionDisabled) {
      onSelect(contactId, !isSelected)
    }
  }

  const renderPhoneNumber = () => {
    if (!contact.fullPhoneNumber) {
      return null
    }

    const { country = '' } = parsePhoneNumber(contact.fullPhoneNumber)
    return (
      <IconText>
        <Box sx={identityVisualSx}>
          <CountryFlag code={country} />
        </Box>
        <Typography component="span">{formatPhoneNumber(`+${contact.fullPhoneNumber}`)}</Typography>
      </IconText>
    )
  }

  return (
    <Card
      data-testid="contact-subscription-card"
      variant="outlined"
      sx={{
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: isSelectionDisabled ? 'default' : 'pointer',
        minWidth: 0,
        overflow: 'hidden',
      }}
      onClick={handleSelect}
    >
      <CardContent sx={{ '&:last-child': { paddingBottom: 2 }, padding: 2 }}>
        <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1, minWidth: 0 }}>
          <Tooltip title={selectionDisabledReason}>
            <span onClick={(event) => event.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                disabled={isSelectionDisabled}
                slotProps={{ input: { 'aria-label': `Select ${contactName || `contact ${contactId}`}` } }}
                sx={{ marginLeft: -1, marginTop: -1 }}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => onSelect(contactId, event.target.checked)}
              />
            </span>
          </Tooltip>
          <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
            <IconText>
              <Box sx={identityVisualSx}>
                <Icon>person_outline</Icon>
              </Box>
              <Typography component="span" noWrap title={contactName}>
                {contactName}
              </Typography>
            </IconText>
            {renderPhoneNumber()}
          </Box>
        </Box>
        <Divider sx={{ marginY: 1.5 }} />
        <IconText>
          <Icon>send</Icon>
          {activeId > 0 || totalCount > 0 ? (
            <Typography className="sub-status">
              {activeId === 0 && totalCount > 0 && 'Completed'}
              {activeId > 0 && 'In progress'}
              <br />
              <Typography className="sub-stats" color="textSecondary" component="span">
                {doneCount} finished, {unsubbedCount} unsubscribed, {errorCount} failure
              </Typography>
            </Typography>
          ) : (
            <Typography className="sub-status">Not subscribed</Typography>
          )}
        </IconText>
      </CardContent>
    </Card>
  )
}

const MobileGroupSegmentSubCard: React.FC<MobileGroupSegmentSubCardProps> = (props) => {
  const { group, isSelected, root, selectionDisabledReason, timezone, onSelect } = props
  const isSelectionDisabled = !!selectionDisabledReason
  const itemLabel = root === 'segments' ? 'segment' : 'group'

  const handleSelect = () => {
    if (!isSelectionDisabled) {
      onSelect(group.id, !isSelected)
    }
  }

  return (
    <Card
      data-testid={`${itemLabel}-subscription-card`}
      variant="outlined"
      sx={{
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: isSelectionDisabled ? 'default' : 'pointer',
        minWidth: 0,
        overflow: 'hidden',
      }}
      onClick={handleSelect}
    >
      <CardContent sx={{ '&:last-child': { paddingBottom: 2 }, padding: 2 }}>
        <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1, minWidth: 0 }}>
          <Tooltip title={selectionDisabledReason}>
            <span onClick={(event) => event.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                disabled={isSelectionDisabled}
                slotProps={{ input: { 'aria-label': `Select ${itemLabel} ${group.name}` } }}
                sx={{ marginLeft: -1, marginTop: -1 }}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => onSelect(group.id, event.target.checked)}
              />
            </span>
          </Tooltip>
          <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
            <Typography component="span" fontWeight={600} noWrap title={group.name}>
              {group.name}
            </Typography>
            <Typography color="textSecondary" component="span" variant="body2">
              Created {formatDateTime(group.createdAt, timezone)}
            </Typography>
            <IconText>
              <Icon>group</Icon>
              <Typography component="span">
                {group.numberOfContacts} {group.numberOfContacts === 1 ? 'contact' : 'contacts'}
              </Typography>
            </IconText>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

const MobileGroupSubCard: React.FC<MobileGroupSubCardProps> = (props) => (
  <MobileGroupSegmentSubCard {...props} root="groups" />
)

const MobileSegmentSubCard: React.FC<MobileGroupSubCardProps> = (props) => (
  <MobileGroupSegmentSubCard {...props} root="segments" />
)

const identityVisualSx = {
  alignItems: 'center',
  display: 'flex',
  flex: '0 0 24px',
  justifyContent: 'center',
  width: 24,
}

export { MobileContactSubCard, MobileGroupSubCard, MobileSegmentSubCard }
